import { deletePushSubscription, forgetPushSubscription, savePushSubscription } from '../api/push.api';

import type { PushEnvironment, PushPermission, PushPort } from './device';
import { forgetThisDevice } from './device';
import { configuredVapidKey } from './vapidKey';

export const ALERT_WORKER_URL = '/worker.js';

const STANDALONE = '(display-mode: standalone)';

const isStandalone = (): boolean => {
  const byDisplayMode = typeof window.matchMedia === 'function' && window.matchMedia(STANDALONE).matches;
  return byDisplayMode || (navigator as Navigator & { standalone?: boolean }).standalone === true;
};

const isSupported = (): boolean =>
  configuredVapidKey() !== null && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

export const browserEnvironment = (): PushEnvironment => {
  const supported = isSupported();
  return { supported, standalone: isStandalone(), permission: supported ? Notification.permission : 'default' };
};

export const registerAlertWorker = (): Promise<ServiceWorkerRegistration> => navigator.serviceWorker.register(ALERT_WORKER_URL);

export const closeShownAlerts = async (tag: string): Promise<void> => {
  if (tag === '') return;
  const registration = await navigator.serviceWorker.getRegistration(ALERT_WORKER_URL);
  if (registration === undefined || typeof registration.getNotifications !== 'function') return;
  const shown = await registration.getNotifications({ tag });
  shown.forEach((notification) => notification.close());
};

export const requestNotificationPermission = (): Promise<PushPermission> =>
  'Notification' in window ? Notification.requestPermission() : Promise.resolve('denied');

const NO_KEY = (): Promise<string> => Promise.reject(new Error('No push key is needed to forget this device'));

export const browserPushPort = (vapidKey: () => Promise<string>): PushPort => ({
  environment: browserEnvironment,
  register: registerAlertWorker,
  existingRegistration: () => navigator.serviceWorker.getRegistration(ALERT_WORKER_URL),
  vapidKey,
  save: savePushSubscription,
  remove: deletePushSubscription,
  userAgent: () => navigator.userAgent,
});

const configuredKey = (): Promise<string> => {
  const key = configuredVapidKey();
  return key === null ? Promise.reject(new Error('VITE_VAPID_PUBLIC_KEY is not set')) : Promise.resolve(key);
};

export const BROWSER_PUSH: PushPort = browserPushPort(configuredKey);

export const FORGET_WITHIN_MS = 3_000;

const forgetQuietly = async (): Promise<void> => {
  try {
    await forgetThisDevice({ ...browserPushPort(NO_KEY), remove: forgetPushSubscription });
  } catch {
    return;
  }
};

export const forgetThisBrowser = (withinMs: number = FORGET_WITHIN_MS): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, withinMs);
  });
  return Promise.race([forgetQuietly(), deadline]).finally(() => clearTimeout(timer));
};
